import { useDndContext, useDroppable } from '@dnd-kit/core';
import { animated, useSprings } from '@react-spring/web';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import { useContextMenu } from 'components/common/context-menu-manager';
import StructureLoadingSpinner from 'components/common/structure-loading-spinner';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  CondenserPhotoData,
  ContextMenuItemType,
  DesignRuleValidationIssue,
  FootprintPolygonsType,
  FootprintShapeType,
  GridObjectType,
  GridSpaceDataType,
  LineSetType,
  PositionAndDimensionsType,
  PositionType,
  SelectionBoxType,
  StructureImageTransformType,
  ZoneOutlineData
} from 'data/types';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  BoundingBox,
  computeAutoFitBoundingBox
} from 'utils/auto-fit-bounding-box';
import { ZoneConcealedDuctLayout } from 'utils/concealed-duct-geometry';
import { FootprintMode } from 'utils/footprint-mode';
import { FootprintScalingTransform } from 'utils/footprint-scaling-gesture';
import { isWallMountedAirHandler } from 'utils/air-handler-mounting';
import {
  calculateProportionalScalePositions,
  getAdjacentSpaces,
  getSelectionBoundingBox,
  ProportionalScaleHandle
} from 'utils/grid';
import {
  computeContentPixelBounds,
  computeMinZoom,
  zoomAfterWheelDelta
} from 'utils/grid-zoom';
import {
  dispatchSyntheticContextMenu,
  hasMovedPastPanThreshold,
  isEventInsideElement,
  shouldStopContextMenuPropagation,
  shouldSuppressNativeContextMenuAfterGesture
} from 'utils/right-click-pan';
import {
  getCenteredLabelPosition,
  getLargestLabelPosition,
  getSectionLabelGroupForSpace,
  getSectionLabelGroups
} from 'utils/section-labels';
import { resolveSpaceLabelFit } from 'utils/space-label-fit';
import { collectSpaceLabelObstacles } from 'utils/space-label-obstacles';
import { findSpaceById } from 'utils/structure';
import { isErrorSeverity } from 'utils/validation';
import { getZonePhotoCenter } from 'utils/zone-outline';
import DrawnSpacePreviewLayer from './drawn-space-preview-layer';
import FootprintDrawingLayer from './footprint-drawing-layer';
import FootprintOverlay from './footprint-overlay';
import FootprintScalingLayer from './footprint-scaling-layer';
import FootprintShapesLayer from './footprint-shapes-layer';
import GridDuctRuns from './grid-duct-runs';
import GridLineSet from './grid-lineset';
import GridObject from './grid-object';
import GridRuler from './grid-ruler';
import GridSpace from './grid-space';
import LayoutImageUnderlay from './layout-image-underlay';
import ProductPhoto from './product-photo';
import { STRUCTURE_GRID_ZONE_OUTLINES_Z_CLASS } from './structure-grid-stacking';
import ZoneOutline, {
  AIR_HANDLER_PHOTO_HEIGHT,
  AIR_HANDLER_PHOTO_WIDTH
} from './zone-outline';

type StructureGridProps = {
  gridSpaces: GridSpaceDataType[];
  overlaySpaces?: GridSpaceDataType[];
  overlayObjects?: GridObjectType[];
  objects?: GridObjectType[];
  draggedSpaceIds?: string[];
  lineSets?: LineSetType[];
  concealedDuctLayouts?: ZoneConcealedDuctLayout[];
  currentLevelIndex?: number;
  configurationId?: string;
  currentLevelUniqueId?: string;
  designRuleIssues?: DesignRuleValidationIssue[];
  mode?:
    | 'spaces'
    | 'conditioning'
    | 'ceilings'
    | 'fenestration'
    | 'electrical'
    | 'summary'
    | 'static'
    | 'zones'
    | 'appliances';
  layoutUnderlay?: boolean;
  layoutImage?: string;
  layoutImageTransform?: StructureImageTransformType;
  layoutUnderlayOpacity?: number;
  gridSpaceOpacity?: number;
  overlaySpacesOpacity?: number;
  gridLinesOpacity?: number;
  selectedSpaceIds?: Set<string>;
  onSpaceSelect?: (spaceId: string, isMultiSelect: boolean) => void;
  onMarqueeSelect?: (spaceIds: string[], isAdditive: boolean) => void;
  onClearSelection?: () => void;
  onMultiSpaceResize?: (
    updates: Map<string, PositionAndDimensionsType>
  ) => void;
  onLayoutImageTransformChange?: (
    transform: StructureImageTransformType
  ) => void;
  onLayoutImageTransformEnd?: () => void;
  isLayoutImageSelected?: boolean;
  onLayoutImageSelect?: () => void;
  onLayoutImageDeselect?: () => void;
  onLayoutImageLoad?: (dims: {
    naturalWidth: number;
    naturalHeight: number;
  }) => void;
  showSectionHighlighting?: boolean;
  showSpaceBtus?: boolean;
  zoneOutlines?: ZoneOutlineData[];
  condenserPhotos?: CondenserPhotoData[];
  onZoneSpaceClick?: (spaceId: string, sectionId: string) => void;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  panOffset?: { x: number; y: number };
  onPanOffsetChange?: (offset: { x: number; y: number }) => void;
  enableContextMenuInStaticMode?: boolean;
  readOnly?: boolean;
  contextMenuFilter?: (items: ContextMenuItemType[]) => ContextMenuItemType[];
  getZoneValidation?: (zoneId: string) => {
    isValid: boolean;
    hasWarning: boolean;
    messages: string[];
  };
  noTopRounding?: boolean;
  footprintPolygons?: FootprintPolygonsType | null;
  levelBelowFootprint?: FootprintPolygonsType | null;
  levelAboveFootprint?: FootprintPolygonsType | null;
  footprintMode?: FootprintMode;
  footprintDrawingResetKey?: number;
  footprintShapes?: FootprintShapeType[];
  onFootprintShapeChange?: (
    shapeIndex: number,
    points: PositionType[],
    vertexEdited: boolean
  ) => void;
  onDeleteFootprintShape?: (shapeIndex: number) => void;
  showFootprintArea?: boolean;
  onFootprintStrokeComplete?: (points: PositionType[]) => void;
  onFootprintDragStart?: () => void;
  onFootprintDragEnd?: () => void;
  onFootprintScaleTransformChange?: (
    transform: FootprintScalingTransform
  ) => void;
  onFootprintScaleTransformEnd?: () => void;
  spaceDrawingMode?: boolean;
  onSpaceStrokeComplete?: (points: PositionType[]) => void;
  pendingDrawnSpace?: PositionType[] | null;
  hoveredZoneId?: string | null;
  onZoneHover?: (zoneId: string | null) => void;
  showRuler?: boolean;
  autoFit?: boolean;
  autoFitBoundingBox?: BoundingBox | null;
  autoFitPaddingTiles?: number;
  onBackgroundClick?: (gridPoint: { x: number; y: number }) => void;
};

const StructureGrid = forwardRef<HTMLDivElement, StructureGridProps>(
  function StructureGrid(
    {
      gridSpaces,
      overlaySpaces = [],
      overlayObjects = [],
      objects = [],
      draggedSpaceIds = [],
      lineSets = [],
      concealedDuctLayouts = [],
      currentLevelIndex,
      configurationId,
      currentLevelUniqueId,
      designRuleIssues,
      mode = 'summary',
      layoutUnderlay = false,
      layoutImage,
      layoutImageTransform,
      layoutUnderlayOpacity = 50,
      gridSpaceOpacity = 100,
      overlaySpacesOpacity = 50,
      gridLinesOpacity = 100,
      selectedSpaceIds = new Set(),
      onSpaceSelect,
      onMarqueeSelect,
      onClearSelection,
      onMultiSpaceResize,
      showSectionHighlighting = false,
      showSpaceBtus = false,
      onLayoutImageTransformChange,
      onLayoutImageTransformEnd,
      isLayoutImageSelected = false,
      onLayoutImageSelect,
      onLayoutImageDeselect,
      onLayoutImageLoad,
      zoneOutlines = [],
      condenserPhotos = [],
      onZoneSpaceClick,
      zoom: zoomProp,
      onZoomChange,
      panOffset: panOffsetProp,
      onPanOffsetChange,
      enableContextMenuInStaticMode = false,
      readOnly = false,
      contextMenuFilter,
      getZoneValidation,
      noTopRounding = false,
      footprintPolygons,
      levelBelowFootprint,
      levelAboveFootprint,
      footprintMode,
      footprintDrawingResetKey,
      footprintShapes,
      onFootprintShapeChange,
      onDeleteFootprintShape,
      showFootprintArea = false,
      onFootprintStrokeComplete,
      onFootprintDragStart,
      onFootprintDragEnd,
      onFootprintScaleTransformChange,
      onFootprintScaleTransformEnd,
      spaceDrawingMode = false,
      onSpaceStrokeComplete,
      pendingDrawnSpace = null,
      hoveredZoneId,
      onZoneHover,
      showRuler = false,
      autoFit = false,
      autoFitBoundingBox: autoFitBoundingBoxProp,
      autoFitPaddingTiles,
      onBackgroundClick
    },
    ref
  ) {
    const footprintDrawingMode = footprintMode === 'draw';
    const footprintScalingMode = footprintMode === 'scale';
    const footprintShapesEditMode = footprintMode === 'edit';
    const SECTION_LABEL_PADDING = 8;
    const { closeContextMenu } = useContextMenu();
    const { easyMode } = useAppSelector((state) => state.ui);
    const { structure } = useAppSelector((state) => state.structure);
    const { configurations } = useAppSelector((state) => state.configurations);
    const { estimatingStructure, analyzingStructureImages } = useAppSelector(
      (state) => state.ui
    );
    const { arrangingLoading } = useAppSelector((state) => state.ai);
    const loading = estimatingStructure || arrangingLoading;
    const { setNodeRef, isOver } = useDroppable({
      id: 'grid-container'
    });
    const { active } = useDndContext();

    const getValidationStatusForObject = useCallback(
      (object: GridObjectType) => {
        if (!designRuleIssues || designRuleIssues.length === 0)
          return undefined;

        let entityIssues: DesignRuleValidationIssue[] = [];

        if (object.type === 'condenser') {
          entityIssues = designRuleIssues.filter((i) =>
            i.failures.some((f) => f.condenserUniqueId === object.itemId)
          );
        } else if (object.type === 'air-handler') {
          const config = configurations.find(
            (c) => c.uniqueId === configurationId
          );
          if (!config) return undefined;

          let zoneUniqueId: string | undefined;
          for (const condenser of config.condensers) {
            for (const zone of condenser.zones) {
              if (zone.airHandler?.uniqueId === object.itemId) {
                zoneUniqueId = zone.uniqueId;
                break;
              }
            }
            if (zoneUniqueId) break;
          }
          if (!zoneUniqueId) return undefined;

          entityIssues = designRuleIssues.filter((i) =>
            i.failures.some((f) => f.zoneUniqueId === zoneUniqueId)
          );
        } else {
          return undefined;
        }

        if (entityIssues.length === 0) return undefined;

        const errors = entityIssues.filter((i) => isErrorSeverity(i.severity));
        const warnings = entityIssues.filter(
          (i) => !isErrorSeverity(i.severity)
        );

        return {
          isValid: errors.length === 0,
          hasWarning: warnings.length > 0,
          messages: [
            ...errors.map((i) => `Error: ${i.message}`),
            ...warnings.map((i) => `Warning: ${i.message}`)
          ]
        };
      },
      [designRuleIssues, configurations, configurationId]
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const layoutImageNaturalDimsRef = useRef<{
      naturalWidth: number;
      naturalHeight: number;
    } | null>(null);
    const [selectionBox, setSelectionBox] = useState<SelectionBoxType | null>(
      null
    );
    const [pendingSelectionIds, setPendingSelectionIds] = useState<Set<string>>(
      new Set()
    );
    const isSelectingRef = useRef(false);

    const [isMultiResizing, setIsMultiResizing] = useState(false);
    const [activeScaleHandle, setActiveScaleHandle] =
      useState<ProportionalScaleHandle | null>(null);
    const multiResizeStartRef = useRef<{ x: number; y: number } | null>(null);
    const initialBoundingBoxRef = useRef<PositionAndDimensionsType | null>(
      null
    );
    const initialSpacePositionsRef = useRef<
      Map<string, PositionAndDimensionsType>
    >(new Map());

    const [isSpacebarDown, setIsSpacebarDown] = useState(false);
    const [isPanning, setIsPanning] = useState(false);
    const rightClickPanMovedRef = useRef(false);
    const pendingRightClickRef = useRef<{
      target: EventTarget;
      clientX: number;
      clientY: number;
    } | null>(null);
    const isOpeningDeferredMenuRef = useRef(false);
    const suppressNativeContextMenuRef = useRef(false);
    const suppressNativeContextMenuTimeoutRef = useRef<number | null>(null);
    const [internalPanOffset, setInternalPanOffset] = useState({ x: 0, y: 0 });
    const panOffset = panOffsetProp ?? internalPanOffset;
    const setPanOffset = onPanOffsetChange ?? setInternalPanOffset;
    const [internalZoom, setInternalZoom] = useState(1);
    const zoom = zoomProp ?? internalZoom;
    const setZoom = onZoomChange ?? setInternalZoom;
    const MIN_ZOOM = 0.25;
    const MAX_ZOOM = 4;
    const panStartRef = useRef<{
      mouseX: number;
      mouseY: number;
      offsetX: number;
      offsetY: number;
    } | null>(null);
    const panOffsetRef = useRef(panOffset);
    const zoomRef = useRef(zoom);
    const sectionHoverLeaveTimeoutRef = useRef<number | null>(null);
    const sectionLabelKeysRef = useRef<string>('');
    const sectionLabelMeasureRefs = useRef<Map<string, HTMLDivElement>>(
      new Map()
    );
    const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(
      null
    );
    const [sectionLabelSizes, setSectionLabelSizes] = useState<
      Record<string, { width: number; height: number }>
    >({});

    const selectionBoundingBox = useMemo(() => {
      return getSelectionBoundingBox(selectedSpaceIds, gridSpaces);
    }, [selectedSpaceIds, gridSpaces]);

    const sectionLabelGroups = useMemo(
      () => getSectionLabelGroups(gridSpaces, structure),
      [gridSpaces, structure]
    );
    const sectionLabelEntries = useMemo(
      () => Array.from(sectionLabelGroups.values()),
      [sectionLabelGroups]
    );

    const spaceLabelObstaclesById = useMemo(
      () =>
        collectSpaceLabelObstacles(
          gridSpaces,
          objects,
          zoneOutlines,
          condenserPhotos
        ),
      [gridSpaces, objects, zoneOutlines, condenserPhotos]
    );

    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    const combineRefs = useCallback(
      (node: HTMLDivElement | null) => {
        setNodeRef(node);
        (
          containerRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node;
      },
      [setNodeRef]
    );

    const getSpacesInSelectionBox = useCallback(
      (box: SelectionBoxType): string[] => {
        const screenLeft = Math.min(box.startX, box.currentX);
        const screenRight = Math.max(box.startX, box.currentX);
        const screenTop = Math.min(box.startY, box.currentY);
        const screenBottom = Math.max(box.startY, box.currentY);

        const left = (screenLeft - panOffset.x) / zoom;
        const right = (screenRight - panOffset.x) / zoom;
        const top = (screenTop - panOffset.y) / zoom;
        const bottom = (screenBottom - panOffset.y) / zoom;

        return gridSpaces
          .filter((space) => {
            const spaceLeft = space.position.x;
            const spaceRight = space.position.x + space.position.width;
            const spaceTop = space.position.y;
            const spaceBottom = space.position.y + space.position.height;

            return (
              spaceLeft < right &&
              spaceRight > left &&
              spaceTop < bottom &&
              spaceBottom > top
            );
          })
          .map((space) => space.spaceId);
      },
      [gridSpaces, panOffset, zoom]
    );

    const handleLayoutImageSelect = useCallback(() => {
      onLayoutImageSelect?.();
      onClearSelection?.();
    }, [onLayoutImageSelect, onClearSelection]);

    const handleLayoutImageDeselect = useCallback(() => {
      onLayoutImageDeselect?.();
    }, [onLayoutImageDeselect]);

    const handleLayoutImageTransformChangeInternal = useCallback(
      (transform: {
        scale?: number;
        rotation?: number;
        positionX?: number;
        positionY?: number;
      }) => {
        const update: StructureImageTransformType = {};
        if (transform.scale !== undefined) update.scale = transform.scale;
        if (transform.rotation !== undefined)
          update.rotation = transform.rotation;
        if (transform.positionX !== undefined)
          update.startPositionX = Math.round(transform.positionX);
        if (transform.positionY !== undefined)
          update.startPositionY = Math.round(transform.positionY);
        onLayoutImageTransformChange?.(update);
      },
      [onLayoutImageTransformChange]
    );

    const handleLayoutImageTransformEndInternal = useCallback(() => {
      onLayoutImageTransformEnd?.();
    }, [onLayoutImageTransformEnd]);

    const handleLayoutImageLoadInternal = useCallback(
      (dims: { naturalWidth: number; naturalHeight: number }) => {
        layoutImageNaturalDimsRef.current = dims;
        onLayoutImageLoad?.(dims);
      },
      [onLayoutImageLoad]
    );

    // Multi-space proportional resize handlers
    const handleMultiResizeStart = useCallback(
      (e: React.MouseEvent, handle: ProportionalScaleHandle) => {
        e.preventDefault();
        e.stopPropagation();

        if (!selectionBoundingBox || selectedSpaceIds.size < 2) return;

        setIsMultiResizing(true);
        setActiveScaleHandle(handle);
        multiResizeStartRef.current = { x: e.clientX, y: e.clientY };
        initialBoundingBoxRef.current = { ...selectionBoundingBox };

        const positions = new Map<string, PositionAndDimensionsType>();
        gridSpaces.forEach((space) => {
          if (selectedSpaceIds.has(space.spaceId)) {
            positions.set(space.spaceId, { ...space.position });
          }
        });
        initialSpacePositionsRef.current = positions;
      },
      [selectionBoundingBox, selectedSpaceIds, gridSpaces]
    );

    const handleMultiResizeMove = useCallback(
      (e: MouseEvent) => {
        if (
          !isMultiResizing ||
          !activeScaleHandle ||
          !multiResizeStartRef.current ||
          !initialBoundingBoxRef.current ||
          !onMultiSpaceResize
        )
          return;

        const deltaX = e.clientX - multiResizeStartRef.current.x;
        const deltaY = e.clientY - multiResizeStartRef.current.y;

        const newPositions = calculateProportionalScalePositions({
          selectedSpaceIds,
          handle: activeScaleHandle,
          deltaX,
          deltaY,
          initialBoundingBox: initialBoundingBoxRef.current,
          initialSpacePositions: initialSpacePositionsRef.current,
          uniform: e.shiftKey
        });

        onMultiSpaceResize(newPositions);
      },
      [
        isMultiResizing,
        activeScaleHandle,
        selectedSpaceIds,
        gridSpaces,
        onMultiSpaceResize
      ]
    );

    const handleMultiResizeEnd = useCallback(() => {
      setIsMultiResizing(false);
      setActiveScaleHandle(null);
      multiResizeStartRef.current = null;
      initialBoundingBoxRef.current = null;
      initialSpacePositionsRef.current = new Map();
    }, []);

    useEffect(() => {
      if (isMultiResizing) {
        window.addEventListener('mousemove', handleMultiResizeMove);
        window.addEventListener('mouseup', handleMultiResizeEnd);
        return () => {
          window.removeEventListener('mousemove', handleMultiResizeMove);
          window.removeEventListener('mouseup', handleMultiResizeEnd);
        };
      }
      return undefined;
    }, [isMultiResizing, handleMultiResizeMove, handleMultiResizeEnd]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        suppressNativeContextMenuRef.current = false;
        if (autoFit) return;

        if (e.button === 2) {
          e.preventDefault();
          closeContextMenu();
          setIsPanning(true);
          rightClickPanMovedRef.current = false;
          pendingRightClickRef.current = {
            target: e.target,
            clientX: e.clientX,
            clientY: e.clientY
          };
          panStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            offsetX: panOffset.x,
            offsetY: panOffset.y
          };
          return;
        }

        if (e.button !== 0) return;

        if (isSpacebarDown) {
          e.preventDefault();
          closeContextMenu();
          setIsPanning(true);
          panStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            offsetX: panOffset.x,
            offsetY: panOffset.y
          };
          return;
        }

        if (!onMarqueeSelect) return;
        if (active) return;
        const target = e.target as HTMLElement;
        if (target.closest('[data-grid-space]')) return;
        if (target.closest('[data-grid-object]')) return;
        if (target.closest('[data-lineset-point]')) return;
        if (target.closest('[data-lineset-line]')) return;
        if (target.closest('[data-cd-endpoint]')) return;
        if (target.closest('[data-scale-handle]')) return;
        if (target.closest('[data-edge-item]')) return;

        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        isSelectingRef.current = true;
        setSelectionBox({
          startX: x,
          startY: y,
          currentX: x,
          currentY: y
        });

        if (isLayoutImageSelected) {
          onLayoutImageDeselect?.();
        }

        if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
          onClearSelection?.();
        }
      },
      [
        autoFit,
        active,
        onClearSelection,
        onMarqueeSelect,
        isLayoutImageSelected,
        onLayoutImageDeselect,
        isSpacebarDown,
        panOffset,
        closeContextMenu
      ]
    );

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isPanning && panStartRef.current) {
          const deltaX = e.clientX - panStartRef.current.mouseX;
          const deltaY = e.clientY - panStartRef.current.mouseY;

          if (
            hasMovedPastPanThreshold(
              panStartRef.current.mouseX,
              panStartRef.current.mouseY,
              e.clientX,
              e.clientY
            )
          ) {
            rightClickPanMovedRef.current = true;
          }

          const newOffsetX = panStartRef.current.offsetX + deltaX;
          const newOffsetY = panStartRef.current.offsetY + deltaY;

          setPanOffset({ x: newOffsetX, y: newOffsetY });
          return;
        }

        if (!isSelectingRef.current || !selectionBox) return;

        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const updatedBox = { ...selectionBox, currentX: x, currentY: y };
        setSelectionBox(updatedBox);

        const width = Math.abs(updatedBox.currentX - updatedBox.startX);
        const height = Math.abs(updatedBox.currentY - updatedBox.startY);

        if (width > 5 || height > 5) {
          const ids = getSpacesInSelectionBox(updatedBox);
          setPendingSelectionIds(new Set(ids));
        } else {
          setPendingSelectionIds(new Set());
        }
      },
      [selectionBox, getSpacesInSelectionBox, isPanning]
    );

    const finishPanGesture = useCallback(
      (button: number) => {
        if (!panStartRef.current && !pendingRightClickRef.current) {
          if (isPanning) {
            setIsPanning(false);
          }
          return;
        }

        const moved = rightClickPanMovedRef.current;
        const pending = pendingRightClickRef.current;
        setIsPanning(false);
        panStartRef.current = null;
        rightClickPanMovedRef.current = false;
        pendingRightClickRef.current = null;

        if (
          shouldSuppressNativeContextMenuAfterGesture(button, pending != null)
        ) {
          suppressNativeContextMenuRef.current = true;
          if (suppressNativeContextMenuTimeoutRef.current != null) {
            window.clearTimeout(suppressNativeContextMenuTimeoutRef.current);
          }
          suppressNativeContextMenuTimeoutRef.current = window.setTimeout(
            () => {
              suppressNativeContextMenuRef.current = false;
              suppressNativeContextMenuTimeoutRef.current = null;
            },
            0
          );
        }

        if (button === 2 && !moved && pending) {
          isOpeningDeferredMenuRef.current = true;
          dispatchSyntheticContextMenu(
            pending.target,
            pending.clientX,
            pending.clientY
          );
          isOpeningDeferredMenuRef.current = false;
        }
      },
      [isPanning]
    );

    const handleMouseUp = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isPanning || e.button === 2) {
          finishPanGesture(e.button);
          return;
        }

        if (!isSelectingRef.current) return;
        isSelectingRef.current = false;

        if (selectionBox) {
          const width = Math.abs(selectionBox.currentX - selectionBox.startX);
          const height = Math.abs(selectionBox.currentY - selectionBox.startY);

          if (width > 5 || height > 5) {
            const selectedIds = getSpacesInSelectionBox(selectionBox);
            if (selectedIds.length > 0) {
              onMarqueeSelect?.(
                selectedIds,
                e.shiftKey || e.ctrlKey || e.metaKey
              );
            }
          }
        }

        setSelectionBox(null);
        setPendingSelectionIds(new Set());
      },
      [
        selectionBox,
        getSpacesInSelectionBox,
        onMarqueeSelect,
        isPanning,
        finishPanGesture
      ]
    );

    useEffect(() => {
      if (autoFit) {
        return undefined;
      }

      const handleNativeContextMenu = (event: Event) => {
        if (isOpeningDeferredMenuRef.current) {
          return;
        }
        if (
          !isEventInsideElement(event.target, containerRef.current) ||
          !shouldStopContextMenuPropagation({
            isOpeningDeferredMenu: false,
            isRightClickPanPending:
              pendingRightClickRef.current != null ||
              panStartRef.current != null,
            suppressNativeContextMenu: suppressNativeContextMenuRef.current
          })
        ) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        suppressNativeContextMenuRef.current = false;
      };

      window.addEventListener('contextmenu', handleNativeContextMenu, true);
      return () => {
        window.removeEventListener(
          'contextmenu',
          handleNativeContextMenu,
          true
        );
      };
    }, [autoFit]);

    useEffect(() => {
      return () => {
        if (suppressNativeContextMenuTimeoutRef.current != null) {
          window.clearTimeout(suppressNativeContextMenuTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        if (!panStartRef.current) {
          return;
        }
        const start = panStartRef.current;
        if (
          hasMovedPastPanThreshold(
            start.mouseX,
            start.mouseY,
            e.clientX,
            e.clientY
          )
        ) {
          rightClickPanMovedRef.current = true;
        }
        setPanOffset({
          x: start.offsetX + (e.clientX - start.mouseX),
          y: start.offsetY + (e.clientY - start.mouseY)
        });
      };

      const handleGlobalMouseUp = (e: MouseEvent) => {
        if (panStartRef.current || pendingRightClickRef.current || isPanning) {
          finishPanGesture(e.button);
        }
        if (isSelectingRef.current) {
          isSelectingRef.current = false;
          setSelectionBox(null);
          setPendingSelectionIds(new Set());
        }
      };

      window.addEventListener('mouseup', handleGlobalMouseUp);
      if (isPanning) {
        window.addEventListener('mousemove', handleGlobalMouseMove);
      }
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('mousemove', handleGlobalMouseMove);
      };
    }, [isPanning, finishPanGesture, setPanOffset]);

    useEffect(() => {
      if (autoFit) return undefined;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.code === 'Space') {
          const target = e.target as HTMLElement;
          const isInputFocused =
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable;
          if (!isInputFocused) {
            e.preventDefault();
            setIsSpacebarDown(true);
          }
        }
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.code === 'Space') {
          setIsSpacebarDown(false);
          if (isPanning) {
            setIsPanning(false);
            panStartRef.current = null;
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }, [autoFit, isPanning]);

    useEffect(() => {
      panOffsetRef.current = panOffset;
    }, [panOffset]);

    useEffect(() => {
      zoomRef.current = zoom;
    }, [zoom]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return undefined;

      let prevWidth = container.clientWidth;
      let prevHeight = container.clientHeight;

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        const newWidth = entry.contentRect.width;
        const newHeight = entry.contentRect.height;

        setContainerSize({ width: newWidth, height: newHeight });

        const deltaW = newWidth - prevWidth;
        const deltaH = newHeight - prevHeight;

        if (deltaW !== 0 || deltaH !== 0) {
          const { current } = panOffsetRef;
          setPanOffset({
            x: current.x + deltaW / 2,
            y: current.y + deltaH / 2
          });
        }

        prevWidth = newWidth;
        prevHeight = newHeight;
      });

      observer.observe(container);
      return () => observer.disconnect();
    }, [setPanOffset]);

    const localAutoFitBoundingBox = useMemo(() => {
      if (!autoFit) return null;
      return computeAutoFitBoundingBox(
        gridSpaces,
        objects,
        zoneOutlines,
        condenserPhotos
      );
    }, [autoFit, gridSpaces, objects, zoneOutlines, condenserPhotos]);

    const effectiveAutoFitBoundingBox =
      autoFitBoundingBoxProp ?? localAutoFitBoundingBox;

    useEffect(() => {
      if (!autoFit || !effectiveAutoFitBoundingBox) return;
      if (containerSize.width === 0 || containerSize.height === 0) return;

      const { minX, minY, maxX, maxY } = effectiveAutoFitBoundingBox;
      const padding = (autoFitPaddingTiles ?? 12) * STRUCTURE_GRID_SIZE;
      const paddedWidth = maxX - minX + padding * 2;
      const paddedHeight = maxY - minY + padding * 2;
      if (paddedWidth <= 0 || paddedHeight <= 0) return;

      const rawZoom = Math.min(
        containerSize.width / paddedWidth,
        containerSize.height / paddedHeight,
        MAX_ZOOM
      );
      const zoomToFit = Math.max(MIN_ZOOM, rawZoom);

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      setZoom(zoomToFit);
      setPanOffset({
        x: containerSize.width / 2 - centerX * zoomToFit,
        y: containerSize.height / 2 - centerY * zoomToFit
      });
    }, [
      autoFit,
      effectiveAutoFitBoundingBox,
      autoFitPaddingTiles,
      containerSize.width,
      containerSize.height,
      setZoom,
      setPanOffset
    ]);

    const getSelectionBoxStyle = useCallback(() => {
      if (!selectionBox) return undefined;

      const left = Math.min(selectionBox.startX, selectionBox.currentX);
      const top = Math.min(selectionBox.startY, selectionBox.currentY);
      const width = Math.abs(selectionBox.currentX - selectionBox.startX);
      const height = Math.abs(selectionBox.currentY - selectionBox.startY);

      return {
        left,
        top,
        width,
        height
      };
    }, [selectionBox]);

    const shouldShowOverlayLabel = (space: GridSpaceDataType) => {
      const currentSpace = findSpaceById(structure, space.spaceId);
      const hasOpenTo = (currentSpace?.openTo?.length ?? 0) > 0;

      if (!hasOpenTo) {
        return true;
      }

      const adjacentSpaces = getAdjacentSpaces(space, overlaySpaces);
      const adjacentOpenToSpaces = Object.values(adjacentSpaces).filter(
        (adj): adj is GridSpaceDataType => {
          if (!adj) return false;
          return (
            (currentSpace?.openTo?.includes(adj.spaceId) ?? false) &&
            adj.label === space.label
          );
        }
      );

      if (adjacentOpenToSpaces.length === 0) {
        return true;
      }

      const allConnectedSpaces = [space, ...adjacentOpenToSpaces];

      const largestArea = Math.max(
        ...allConnectedSpaces.map((s) => s.position.width * s.position.height)
      );

      const largestSpaces = allConnectedSpaces.filter(
        (s) => s.position.width * s.position.height === largestArea
      );

      if (largestSpaces.length === 1) {
        return largestSpaces[0].id === space.id;
      }

      largestSpaces.sort((a, b) => {
        if (a.position.y !== b.position.y) {
          return a.position.y - b.position.y;
        }
        return a.position.x - b.position.x;
      });

      return largestSpaces[0].id === space.id;
    };

    const nonWallMountedAirHandlers = useMemo(
      () =>
        objects.filter(
          (obj) =>
            obj.type === 'air-handler' &&
            obj.airHandlerTypeCode &&
            !isWallMountedAirHandler(obj.airHandlerTypeCode)
        ),
      [objects]
    );

    const sectionLabelTargets = useMemo(
      () =>
        sectionLabelEntries.map((group) => {
          const useLargest = hoveredSectionId === group.sectionId;
          const showSectionDimensions =
            !easyMode && mode !== 'static' && mode !== 'zones';
          const sectionDimensionsText = `${Math.round(
            group.largestSpace.position.width / 10
          )}ft × ${Math.round(group.largestSpace.position.height / 10)}ft`;
          const labelBox = useLargest
            ? group.largestSpace.position
            : group.bounds;
          const labelFit = resolveSpaceLabelFit({
            label: group.label,
            spaceWidth: labelBox.width,
            spaceHeight: labelBox.height,
            zoom,
            showDimensions: showSectionDimensions,
            dimensionsText: sectionDimensionsText,
            allowWrap: !useLargest
          });
          const centeredPosition = getCenteredLabelPosition(
            group,
            sectionLabelSizes[group.key],
            { padding: SECTION_LABEL_PADDING }
          );
          const largestPosition = getLargestLabelPosition(group);
          const x = useLargest ? largestPosition.x : centeredPosition.x;
          let y = useLargest ? largestPosition.y : centeredPosition.y;

          const labelSize = sectionLabelSizes[group.key];
          const labelHalfW = (labelSize?.width ?? 0) / 2;
          const labelHalfH = (labelSize?.height ?? 0) / 2;
          const ahSize = 3 * STRUCTURE_GRID_SIZE;
          const gap = 4;

          for (const ah of nonWallMountedAirHandlers) {
            const ahLeft = ah.position.x * STRUCTURE_GRID_SIZE;
            const ahTop = ah.position.y * STRUCTURE_GRID_SIZE;

            const overlapsX =
              ahLeft < x + labelHalfW && ahLeft + ahSize > x - labelHalfW;
            const overlapsY =
              ahTop < y + labelHalfH && ahTop + ahSize > y - labelHalfH;

            if (overlapsX && overlapsY) {
              y = ahTop - labelHalfH - gap;
              break;
            }
          }

          for (const zoneData of zoneOutlines) {
            if (zoneData.spacePositions.length === 1) {
              const photoCenter = getZonePhotoCenter(zoneData, gridSpaces);
              if (photoCenter) {
                const photoLeft = photoCenter.x - AIR_HANDLER_PHOTO_WIDTH / 2;
                const photoTop = photoCenter.y - AIR_HANDLER_PHOTO_HEIGHT / 2;
                const overlapsX =
                  photoLeft < x + labelHalfW &&
                  photoLeft + AIR_HANDLER_PHOTO_WIDTH > x - labelHalfW;
                const overlapsY =
                  photoTop < y + labelHalfH &&
                  photoTop + AIR_HANDLER_PHOTO_HEIGHT > y - labelHalfH;
                if (overlapsX && overlapsY) {
                  y = photoTop - labelHalfH - gap;
                  break;
                }
              }
            }
          }

          return {
            key: group.key,
            group,
            useLargest,
            displayLabel: labelFit.displayLabel,
            largestLabelMaxWidth: Math.max(
              24,
              (group.largestSpace.position.width - SECTION_LABEL_PADDING * 2) *
                zoom
            ),
            x,
            y
          };
        }),
      [
        hoveredSectionId,
        sectionLabelEntries,
        sectionLabelSizes,
        nonWallMountedAirHandlers,
        zoneOutlines,
        easyMode,
        mode,
        zoom
      ]
    );
    const sectionLabelKeys = useMemo(
      () => sectionLabelTargets.map((target) => target.key).join('|'),
      [sectionLabelTargets]
    );

    const [sectionLabelSprings, sectionLabelApi] = useSprings(
      sectionLabelTargets.length,
      (index) => ({
        x: sectionLabelTargets[index]?.x ?? 0,
        y: sectionLabelTargets[index]?.y ?? 0,
        config: { tension: 230, friction: 28 }
      }),
      [sectionLabelTargets.length]
    );

    useEffect(() => {
      const shouldAnimate = sectionLabelKeysRef.current === sectionLabelKeys;
      sectionLabelKeysRef.current = sectionLabelKeys;
      sectionLabelApi.start((index) => ({
        x: sectionLabelTargets[index]?.x ?? 0,
        y: sectionLabelTargets[index]?.y ?? 0,
        immediate: !shouldAnimate
      }));
    }, [sectionLabelApi, sectionLabelTargets, sectionLabelKeys]);

    const handleSectionHover = useCallback(
      (sectionId: string, isHovered: boolean) => {
        if (sectionHoverLeaveTimeoutRef.current !== null) {
          window.clearTimeout(sectionHoverLeaveTimeoutRef.current);
          sectionHoverLeaveTimeoutRef.current = null;
        }

        if (isHovered) {
          setHoveredSectionId(sectionId);
          return;
        }

        sectionHoverLeaveTimeoutRef.current = window.setTimeout(() => {
          setHoveredSectionId((currentHoveredSectionId) =>
            currentHoveredSectionId === sectionId
              ? null
              : currentHoveredSectionId
          );
          sectionHoverLeaveTimeoutRef.current = null;
        }, 40);
      },
      []
    );

    useEffect(() => {
      setHoveredSectionId(null);
    }, [currentLevelUniqueId]);

    useLayoutEffect(() => {
      const nextSizes: Record<string, { width: number; height: number }> = {};
      sectionLabelEntries.forEach((group) => {
        const node = sectionLabelMeasureRefs.current.get(group.key);
        if (!node) {
          return;
        }
        const rect = node.getBoundingClientRect();
        nextSizes[group.key] = {
          width: rect.width / zoom,
          height: rect.height / zoom
        };
      });
      setSectionLabelSizes((currentSizes) => {
        const currentKeys = Object.keys(currentSizes).sort();
        const nextKeys = Object.keys(nextSizes).sort();
        if (
          currentKeys.length !== nextKeys.length ||
          currentKeys.some((key, index) => key !== nextKeys[index])
        ) {
          return nextSizes;
        }
        const changed = nextKeys.some((key) => {
          const current = currentSizes[key];
          const next = nextSizes[key];
          return (
            !current ||
            Math.abs(current.width - next.width) > 0.5 ||
            Math.abs(current.height - next.height) > 0.5
          );
        });
        return changed ? nextSizes : currentSizes;
      });
    }, [sectionLabelEntries, zoom, easyMode, mode]);

    useEffect(() => {
      return () => {
        if (sectionHoverLeaveTimeoutRef.current !== null) {
          window.clearTimeout(sectionHoverLeaveTimeoutRef.current);
        }
      };
    }, []);

    const getCursorStyle = () => {
      if (autoFit) return undefined;
      if (isPanning) return 'grabbing';
      if (isSpacebarDown) return 'grab';
      return undefined;
    };

    const handleBackgroundClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!onBackgroundClick) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const worldX = (e.clientX - rect.left - panOffset.x) / zoom;
        const worldY = (e.clientY - rect.top - panOffset.y) / zoom;
        onBackgroundClick({
          x: worldX / STRUCTURE_GRID_SIZE,
          y: worldY / STRUCTURE_GRID_SIZE
        });
      },
      [onBackgroundClick, panOffset, zoom]
    );

    const handleContextMenuCapture = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        const isOpeningDeferredMenu = isOpeningDeferredMenuRef.current;
        if (
          shouldStopContextMenuPropagation({
            isOpeningDeferredMenu,
            isRightClickPanPending:
              pendingRightClickRef.current != null ||
              panStartRef.current != null,
            suppressNativeContextMenu: suppressNativeContextMenuRef.current
          })
        ) {
          e.stopPropagation();
        }
        if (!isOpeningDeferredMenu) {
          suppressNativeContextMenuRef.current = false;
        }
      },
      []
    );

    const handleContextMenu = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
      },
      []
    );

    const handleWheel = useCallback(
      (e: WheelEvent) => {
        if (active) return;

        e.preventDefault();

        const rect = containerRef.current?.getBoundingClientRect();
        const currentZoom = zoomRef.current;

        const contentBounds = computeContentPixelBounds({
          footprintPolygons,
          imageTransform: layoutImageTransform,
          imageDims: layoutImageNaturalDimsRef.current
        });
        const dynamicMinZoom = rect
          ? computeMinZoom(contentBounds, {
              width: rect.width,
              height: rect.height
            })
          : MIN_ZOOM;
        const minBound = Math.max(MIN_ZOOM, dynamicMinZoom);
        const lowerBound = Math.min(minBound, currentZoom);

        const newZoom = zoomAfterWheelDelta(
          currentZoom,
          e.deltaY,
          e.deltaMode,
          { min: lowerBound, max: MAX_ZOOM }
        );

        if (newZoom !== currentZoom) {
          if (rect) {
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            const currentPan = panOffsetRef.current;

            const worldX = (mouseX - currentPan.x) / currentZoom;
            const worldY = (mouseY - currentPan.y) / currentZoom;

            const newPanX = mouseX - worldX * newZoom;
            const newPanY = mouseY - worldY * newZoom;

            setPanOffset({ x: newPanX, y: newPanY });
          }
          setZoom(newZoom);
        }
      },
      [active, setZoom, setPanOffset, footprintPolygons, layoutImageTransform]
    );

    useEffect(() => {
      if (autoFit) return undefined;
      const container = containerRef.current;
      if (!container) return undefined;

      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }, [autoFit, handleWheel]);

    return (
      <div
        ref={combineRefs}
        className={`relative h-full w-full overflow-hidden bg-white ${
          noTopRounding ? 'rounded-b-lg' : 'rounded-lg'
        } ${isOver ? 'bg-green-50' : ''}`}
        id='structure-grid'
        style={{ cursor: getCursorStyle() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onContextMenuCapture={handleContextMenuCapture}
        onContextMenu={handleContextMenu}
      >
        <div
          className='absolute inset-0'
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            transformOrigin: '0 0'
          }}
        >
          {layoutUnderlay && layoutImage && (
            <LayoutImageUnderlay
              imageUrl={layoutImage}
              opacity={layoutUnderlayOpacity}
              scale={layoutImageTransform?.scale ?? 1}
              rotation={layoutImageTransform?.rotation ?? 0}
              positionX={layoutImageTransform?.startPositionX ?? 0}
              positionY={layoutImageTransform?.startPositionY ?? 0}
              isSelected={isLayoutImageSelected}
              onSelect={handleLayoutImageSelect}
              onDeselect={handleLayoutImageDeselect}
              onTransformChange={handleLayoutImageTransformChangeInternal}
              onTransformEnd={handleLayoutImageTransformEndInternal}
              onImageLoad={handleLayoutImageLoadInternal}
              isPanMode={isSpacebarDown || isPanning}
              zoom={zoom}
            />
          )}
          {!easyMode && gridLinesOpacity > 0 && (
            <div
              className='pointer-events-none absolute z-[1]'
              style={{
                backgroundImage: `
                linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
              `,
                backgroundSize: `${STRUCTURE_GRID_SIZE}px ${STRUCTURE_GRID_SIZE}px`,
                backgroundPosition: '10000px 10000px',
                opacity: gridLinesOpacity / 100,
                left: '-10000px',
                top: '-10000px',
                width: '20000px',
                height: '20000px'
              }}
            />
          )}
          {levelBelowFootprint && levelBelowFootprint.length > 0 && (
            <FootprintOverlay polygons={levelBelowFootprint} faint />
          )}
          {levelAboveFootprint && levelAboveFootprint.length > 0 && (
            <FootprintOverlay polygons={levelAboveFootprint} faint />
          )}
          {footprintPolygons &&
            footprintPolygons.length > 0 &&
            !footprintDrawingMode &&
            !footprintShapesEditMode && (
              <FootprintOverlay
                polygons={footprintPolygons}
                showArea={showFootprintArea}
                pathId='footprint-outline'
              />
            )}
          <div style={{ opacity: overlaySpacesOpacity / 100 }}>
            {overlaySpaces.map((space) => (
              <GridSpace
                key={space.id}
                gridSpace={space}
                allGridSpaces={overlaySpaces}
                overlay
                zoom={zoom}
              />
            ))}
            {overlaySpaces.map((space) => {
              if (!shouldShowOverlayLabel(space)) {
                return null;
              }
              const overlayWidth = easyMode
                ? STRUCTURE_GRID_SIZE * 10
                : space.position.width;
              const overlayHeight = easyMode
                ? STRUCTURE_GRID_SIZE * 10
                : space.position.height;
              const overlayDirection =
                space.overlayDirection === 'above' ? 'Above' : 'Below';
              const overlayFit = resolveSpaceLabelFit({
                label: space.label,
                spaceWidth: overlayWidth,
                spaceHeight: overlayHeight,
                zoom,
                showDimensions: true,
                dimensionsText: overlayDirection
              });
              return (
                <div
                  key={`overlay-label-${space.id}`}
                  className='pointer-events-none absolute z-[10] flex select-none items-center justify-center p-2 text-center text-sm font-medium text-gray-400'
                  style={{
                    left: space.position.x,
                    top: space.position.y,
                    width: overlayWidth,
                    height: overlayHeight
                  }}
                >
                  <div
                    className='flex flex-col items-center'
                    style={{
                      transform: `scale(${1 / (zoom > 0 ? zoom : 1)})`,
                      transformOrigin: 'center'
                    }}
                  >
                    <div>{overlayFit.displayLabel}</div>
                    <div className='text-xs text-gray-400'>
                      {overlayDirection}
                    </div>
                  </div>
                </div>
              );
            })}
            {overlayObjects.map((object) => (
              <GridObject key={object.id} object={object} zoom={zoom} />
            ))}
          </div>
          {gridSpaces.map((gridSpace) => (
            <GridSpace
              key={gridSpace.id}
              id={`grid-space-${gridSpace.spaceId}`}
              gridSpace={gridSpace}
              allGridSpaces={gridSpaces}
              isBeingDragged={draggedSpaceIds.includes(gridSpace.id)}
              mode={mode}
              isSelected={selectedSpaceIds.has(gridSpace.spaceId)}
              isPendingSelection={pendingSelectionIds.has(gridSpace.spaceId)}
              selectedSpaceIds={selectedSpaceIds}
              onSelect={onSpaceSelect}
              opacity={gridSpaceOpacity}
              showSectionHighlighting={showSectionHighlighting}
              onZoneSpaceClick={readOnly ? undefined : onZoneSpaceClick}
              zoom={zoom}
              enableContextMenuInStaticMode={
                enableContextMenuInStaticMode && !readOnly
              }
              contextMenuFilter={contextMenuFilter}
              suppressInternalLabel={
                mode !== 'ceilings' &&
                !!getSectionLabelGroupForSpace(gridSpace, sectionLabelGroups)
              }
              onSectionHover={handleSectionHover}
              labelObstacles={
                spaceLabelObstaclesById.get(gridSpace.spaceId) ?? []
              }
              footprintPolygons={footprintPolygons}
              showSpaceBtus={showSpaceBtus}
            />
          ))}
          {mode !== 'ceilings' &&
            sectionLabelTargets.map((target) => (
              <div
                key={`section-label-measure-${target.key}`}
                ref={(node) => {
                  if (node) {
                    sectionLabelMeasureRefs.current.set(target.key, node);
                  } else {
                    sectionLabelMeasureRefs.current.delete(target.key);
                  }
                }}
                className='pointer-events-none absolute left-0 top-0 z-[-1] flex select-none flex-col items-center justify-center text-center text-sm font-medium text-gray-700 opacity-0'
                style={{
                  transform: `scale(${1 / (zoom > 0 ? zoom : 1)})`,
                  transformOrigin: 'top left'
                }}
              >
                <div className='leading-tight text-black'>
                  {target.displayLabel}
                </div>
                {!easyMode && mode !== 'static' && mode !== 'zones' && (
                  <div className='text-xs text-gray-500'>
                    {Math.round(target.group.largestSpace.position.width / 10)}
                    ft ×{' '}
                    {Math.round(target.group.largestSpace.position.height / 10)}
                    ft
                  </div>
                )}
              </div>
            ))}
          {mode !== 'ceilings' &&
            sectionLabelTargets.map((target, index) => (
              <animated.div
                key={`section-label-${target.key}`}
                className='pointer-events-none absolute z-[30] flex select-none flex-col items-center justify-center text-center text-sm font-medium text-gray-700'
                style={{
                  left: sectionLabelSprings[index]?.x,
                  top: sectionLabelSprings[index]?.y,
                  transform: `translate(-50%, -50%) scale(${
                    1 / (zoom > 0 ? zoom : 1)
                  })`,
                  transformOrigin: 'center',
                  maxWidth: target.useLargest
                    ? `${target.largestLabelMaxWidth}px`
                    : undefined
                }}
              >
                <div className='leading-tight text-black'>
                  {target.displayLabel}
                </div>
                {!easyMode && mode !== 'static' && mode !== 'zones' && (
                  <div className='text-xs text-gray-500'>
                    {Math.round(target.group.largestSpace.position.width / 10)}
                    ft ×{' '}
                    {Math.round(target.group.largestSpace.position.height / 10)}
                    ft
                  </div>
                )}
              </animated.div>
            ))}
          {currentLevelIndex !== undefined && (
            <GridDuctRuns
              layouts={concealedDuctLayouts}
              currentLevelIndex={currentLevelIndex}
              gridSpaces={gridSpaces}
              configurationId={configurationId}
              zoom={zoom}
              readOnly={readOnly}
            />
          )}
          {(zoneOutlines.length > 0 || condenserPhotos.length > 0) && (
            <svg
              className={clsx(
                'pointer-events-none absolute inset-0 h-full w-full overflow-visible',
                STRUCTURE_GRID_ZONE_OUTLINES_Z_CLASS
              )}
              style={{ overflow: 'visible' }}
            >
              {zoneOutlines.map((zoneData) => (
                <ZoneOutline
                  key={zoneData.zoneId}
                  zoneData={zoneData}
                  allGridSpaces={gridSpaces}
                  objects={objects}
                  lineSets={lineSets}
                  levelUniqueId={currentLevelUniqueId}
                  validationStatus={getZoneValidation?.(zoneData.zoneId)}
                  isHovered={hoveredZoneId === zoneData.zoneId}
                  onHover={mode === 'zones' ? undefined : onZoneHover}
                />
              ))}
              {condenserPhotos.map((photo) => (
                <ProductPhoto
                  key={photo.condenserId}
                  uniqueId={`condenser-photo-${photo.condenserId}`}
                  center={photo.position}
                  photoUrl={photo.photoUrl}
                  strokeColor={photo.strokeColor}
                  name={photo.name}
                  descriptors={photo.descriptors}
                />
              ))}
            </svg>
          )}
          {objects.map((object) => (
            <GridObject
              key={object.id}
              object={object}
              configurationId={configurationId}
              gridSpaces={gridSpaces}
              structure={structure}
              validationStatus={getValidationStatusForObject(object)}
              zoom={zoom}
              mode={mode}
              readOnly={readOnly}
            />
          ))}
          {configurationId &&
            lineSets.map((lineset) => (
              <GridLineSet
                key={lineset.uniqueId}
                lineset={lineset}
                configurationId={configurationId}
                currentLevelUniqueId={currentLevelUniqueId}
                structure={structure}
                zoom={zoom}
                readOnly={readOnly}
              />
            ))}
          {(footprintShapesEditMode || footprintDrawingMode) &&
            onFootprintShapeChange &&
            onDeleteFootprintShape && (
              <FootprintShapesLayer
                shapes={footprintShapes ?? []}
                onShapeChange={onFootprintShapeChange}
                onDeleteShape={onDeleteFootprintShape}
                onDragStart={onFootprintDragStart}
                onDragEnd={onFootprintDragEnd}
                zoom={zoom}
                showArea={showFootprintArea}
                readOnly={footprintDrawingMode}
                isLayoutImageSelected={isLayoutImageSelected}
                onDoubleClick={handleLayoutImageSelect}
              />
            )}
          {footprintDrawingMode && onFootprintStrokeComplete && (
            <FootprintDrawingLayer
              key={footprintDrawingResetKey}
              onStrokeComplete={onFootprintStrokeComplete}
              zoom={zoom}
              isLayoutImageSelected={isLayoutImageSelected}
              onLayoutImageDeselect={handleLayoutImageDeselect}
              onDoubleClick={handleLayoutImageSelect}
            />
          )}
          {footprintScalingMode &&
            onFootprintScaleTransformChange &&
            onFootprintScaleTransformEnd && (
              <FootprintScalingLayer
                polygons={footprintPolygons ?? []}
                onTransformChange={onFootprintScaleTransformChange}
                onTransformEnd={onFootprintScaleTransformEnd}
                zoom={zoom}
              />
            )}
          {spaceDrawingMode && onSpaceStrokeComplete && (
            <FootprintDrawingLayer
              onStrokeComplete={onSpaceStrokeComplete}
              zoom={zoom}
              zIndexClass='z-[40]'
            />
          )}
          {pendingDrawnSpace && (
            <DrawnSpacePreviewLayer vertices={pendingDrawnSpace} />
          )}
          {selectionBoundingBox &&
            selectedSpaceIds.size > 1 &&
            mode !== 'static' &&
            active?.data.current?.type !== 'grid-space' && (
              <div
                className='pointer-events-none absolute z-[25]'
                style={{
                  left: selectionBoundingBox.x - 2,
                  top: selectionBoundingBox.y - 2,
                  width: selectionBoundingBox.width + 4,
                  height: selectionBoundingBox.height + 4,
                  border: '2px dashed #3b82f6',
                  borderRadius: '4px'
                }}
              >
                <div
                  className='pointer-events-auto absolute -left-2 -top-2 h-3 w-3 cursor-nw-resize rounded-sm bg-blue-600 hover:bg-blue-700'
                  onMouseDown={(e) => handleMultiResizeStart(e, 'nw')}
                  data-scale-handle='nw'
                />
                <div
                  className='pointer-events-auto absolute -right-2 -top-2 h-3 w-3 cursor-ne-resize rounded-sm bg-blue-600 hover:bg-blue-700'
                  onMouseDown={(e) => handleMultiResizeStart(e, 'ne')}
                  data-scale-handle='ne'
                />
                <div
                  className='pointer-events-auto absolute -bottom-2 -left-2 h-3 w-3 cursor-sw-resize rounded-sm bg-blue-600 hover:bg-blue-700'
                  onMouseDown={(e) => handleMultiResizeStart(e, 'sw')}
                  data-scale-handle='sw'
                />
                <div
                  className='pointer-events-auto absolute -bottom-2 -right-2 h-3 w-3 cursor-se-resize rounded-sm bg-blue-600 hover:bg-blue-700'
                  onMouseDown={(e) => handleMultiResizeStart(e, 'se')}
                  data-scale-handle='se'
                />
              </div>
            )}
        </div>
        {onBackgroundClick && (
          <div
            className='absolute inset-0 z-[45] cursor-crosshair'
            onClick={handleBackgroundClick}
            aria-hidden
          />
        )}
        {loading && (
          <StructureLoadingSpinner
            isAnalyzingImages={analyzingStructureImages}
          />
        )}
        {selectionBox && (
          <div
            className='pointer-events-none absolute z-50 border-2 border-blue-500 bg-blue-200 bg-opacity-30'
            style={getSelectionBoxStyle()}
          />
        )}
        {showRuler && containerSize.width > 0 && (
          <GridRuler
            zoom={zoom}
            panOffset={panOffset}
            containerWidth={containerSize.width}
            containerHeight={containerSize.height}
          />
        )}
      </div>
    );
  }
);

export default StructureGrid;
